"use client";

import { usePathname, useRouter } from "next/navigation";
import { FC, PropsWithChildren, useCallback, useMemo } from "react";
import { DropdownOption, usePoliciesPagesConfig } from "@finbackoffice/site-core";
import { useTranslation } from "react-i18next";
import { TranslationScopes } from "@finbackoffice/enums";
import { IPolicyPagesConfig, PolicyPagesEnum } from "@finbackoffice/fe-core";
import CustomSelect from "components/base/custom-select/CustomSelect";
import Footer from "components/footer/Footer";
import MainContent from "components/MainContent";
import Translate from "components/base/translate/Translate";
import { RouteNames } from "utils/constants";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import styles from "./policies-layout.module.sass";

const PoliciesLayout: FC<PropsWithChildren> = ({ children }) => {
    const router = useRouter();
    const pathname = usePathname();
    const { t } = useTranslation(TranslationScopes.Policies);
    const { enabledPoliciesConfig, enabledPolicySportsbookConfig } = usePoliciesPagesConfig();

    const policiesOptionsMemo = useMemo(() => {
        const result: DropdownOption[] = [];

        enabledPoliciesConfig.forEach((config: IPolicyPagesConfig) => {
            switch (config.name) {
                case PolicyPagesEnum.Privacy:
                    result.push({
                        value: RouteNames.PRIVACY,
                        label: t("policies_menu_privacy"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-privacy.svg`,
                    });
                    break;
                case PolicyPagesEnum.Terms:
                    result.push({
                        value: RouteNames.TERMS,
                        label: t("policies_menu_terms"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-terms.svg`,
                    });
                    break;
                case PolicyPagesEnum.Sports:
                    result.push({
                        value: RouteNames.SPORTSBOOK,
                        label: t("policies_menu_sportsbook"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-sportsbook.svg`,
                    });
                    break;
                case PolicyPagesEnum.Casino:
                    result.push({
                        value: RouteNames.CASINO,
                        label: t("policies_menu_casino"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-casino.svg`,
                    });
                    break;
                case PolicyPagesEnum.Payments:
                    result.push({
                        value: RouteNames.PAYMENTS,
                        label: t("policies_menu_payments"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-payments.svg`,
                    });
                    break;
                case PolicyPagesEnum.ResponsibleGaming:
                    result.push({
                        value: RouteNames.RESPONSIBLE_GAMING,
                        label: t("policies_menu_responsible_gaming"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-responsible-gaming.svg`,
                    });
                    break;
                case PolicyPagesEnum.Cookies:
                    result.push({
                        value: RouteNames.COOKIES,
                        label: t("policies_menu_cookies"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-cookies.svg`,
                    });
                    break;
                case PolicyPagesEnum.SelfExclusion:
                    result.push({
                        value: RouteNames.SELF_EXCLUSION,
                        label: t("policies_menu_self_exclusion"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-self-exclusion.svg`,
                    });
                    break;
                case PolicyPagesEnum.DisputeResolution:
                    result.push({
                        value: RouteNames.DISPUTE_RESOLUTION,
                        label: t("policies_menu_dispute_resolution"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-dispute-resolution.svg`,
                    });
                    break;
                case PolicyPagesEnum.AntiMoneyLaundering:
                    result.push({
                        value: RouteNames.ANTI_MONEY_LAUNDERING,
                        label: t("policies_menu_anti_money_laundering"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-anti-money-laundering.svg`,
                    });
                    break;
                case PolicyPagesEnum.FairnessRngTesting:
                    result.push({
                        value: RouteNames.FAIRNESS_RNG_TESTING,
                        label: t("policies_menu_fairness_rng_testing"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-fairness-rng-testing.svg`,
                    });
                    break;
                case PolicyPagesEnum.Kyc:
                    result.push({
                        value: RouteNames.KYC,
                        label: t("policies_menu_kyc"),
                        svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-kyc.svg`,
                    });
                    break;
                default:
                    break;
            }
        });

        return result;
    }, [enabledPoliciesConfig, t]);

    const getSelected = useCallback(() => {
        const paths = pathname?.split("/");
        const parentRouteIndex = paths?.indexOf(RouteNames.POLICIES) || 0;
        return paths?.[parentRouteIndex + 1] || "";
    }, [pathname]);

    const onSelect = useCallback(
        (val: string) => {
            if (val === RouteNames.SPORTSBOOK) {
                router.push(
                    `/${RouteNames.POLICIES}/${RouteNames.SPORTSBOOK}/${enabledPolicySportsbookConfig?.[0]?.name.toLowerCase().replace(/_/g, "-")}`,
                );
            } else {
                router.push(`/${RouteNames.POLICIES}/${val}`);
            }
        },
        [router, enabledPolicySportsbookConfig],
    );

    return (
        <MainContent>
            <div className={styles.header}>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies.svg`}
                    className="svg-icon"
                />
                <h1>
                    <Translate tid="policies_heading" namespace={TranslationScopes.Policies} />
                </h1>
                <CustomSelect
                    data={policiesOptionsMemo}
                    selected={getSelected()}
                    onChange={onSelect}
                    wrapperClassname={styles.dropdown}
                    variant="dropdown"
                    variantStyle="gradient"
                />
            </div>
            {children}
            <Footer />
        </MainContent>
    );
};

export default PoliciesLayout;
